import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { useOnboardingProgress } from './hooks/useOnboardingProgress';
import { useSubscriptionLock } from '../subscription/hooks/useSubscriptionLock';
import { useAuth } from '../../contexts/AuthContext';
import { useAppContext } from '../../contexts/AppContext';
import {
    resolvePendingMetaOAuth,
    clearMetaOAuthUrlParams,
    clearMetaOAuthSession,
} from '../../services/metaService';
import { persistMetaOAuthCallbackFromUrl } from '../../services/modules/metaSDK';
import { notify } from '../../utils/toast';
import {
    isMetaOAuthFlowActive,
    stashMetaModal,
    META_MODAL_READY_EVENT,
} from './metaReturn';
import {
    clearPendingOnboarding,
    hasPendingOnboarding,
} from './pendingOnboarding';
import { OnboardingGateProvider } from './OnboardingGateContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getSlugFromPage, isPartnerPage } from '../../utils/routeUtils';

/**
 * Gate de onboarding + resolução global do OAuth Meta.
 * Expõe isGateReady para o layout não revelar o dashboard antes do redirect.
 */
export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isCompleted, isLoading } = useOnboardingProgress();
    const { isBlocked: isSubscriptionBlocked } = useSubscriptionLock();
    const { user } = useAuth();
    const { currentPage, setCurrentPage } = useAppContext();
    const { canManageIntegrations } = usePermissions();

    const blockedRef = useRef(isSubscriptionBlocked);
    const completedRef = useRef(isCompleted);
    const loadingRef = useRef(isLoading);
    const setCurrentPageRef = useRef(setCurrentPage);
    blockedRef.current = isSubscriptionBlocked;
    completedRef.current = isCompleted;
    loadingRef.current = isLoading;
    setCurrentPageRef.current = setCurrentPage;

    const gateValue = useMemo(
        () => ({ isGateReady: !user?.id || !isLoading }),
        [user?.id, isLoading],
    );

    const goToOnboardingWhenReady = useCallback(() => {
        if (!user?.id) return;

        let attempts = 0;
        const tick = () => {
            if (completedRef.current) return;
            if (loadingRef.current || (blockedRef.current && attempts++ < 30)) {
                window.setTimeout(tick, 400);
                return;
            }
            if (!blockedRef.current && !completedRef.current) {
                clearPendingOnboarding();
                setCurrentPage('onboarding');
            }
        };
        window.setTimeout(tick, 500);
    }, [user?.id, setCurrentPage]);

    // Cadastro/checkout: aponta onboarding já no mount — evita frame no dashboard.
    // Não competir com SubscriptionGuard (no_plan → /assinatura).
    useEffect(() => {
        if (!user?.id || isMetaOAuthFlowActive()) return;
        if (isSubscriptionBlocked) {
            if (hasPendingOnboarding()) clearPendingOnboarding();
            return;
        }
        if (!hasPendingOnboarding()) return;
        if (currentPage !== 'onboarding') setCurrentPage('onboarding');
    }, [user?.id, currentPage, setCurrentPage, isSubscriptionBlocked]);

    // Captura ?code= Meta antes de qualquer gate (onboarding/subscription) limpar a URL
    useEffect(() => {
        if (!user?.id) return;
        persistMetaOAuthCallbackFromUrl();
    }, [user?.id]);

    // Resolve OAuth Meta globalmente (não depende de /integracoes estar montada)
    useEffect(() => {
        if (!user?.id || !canManageIntegrations) return;
        const params = new URLSearchParams(window.location.search);
        if (params.has('code')) {
            const path = window.location.pathname.replace(/\/$/, '') || '/';
            const igSlug = getSlugFromPage('connections');
            if (path === `/${igSlug}` || path.endsWith(`/${igSlug}`)) return;
        }

        let cancelled = false;

        const run = async () => {
            try {
                // Re-captura se a query ainda existir (race com Strict Mode / remount)
                persistMetaOAuthCallbackFromUrl();
                const data = await resolvePendingMetaOAuth();
                if (!data) return;

                stashMetaModal(data);
                clearMetaOAuthUrlParams();
                clearMetaOAuthSession();
                setCurrentPageRef.current('tracking');

                queueMicrotask(() => {
                    window.dispatchEvent(new CustomEvent(META_MODAL_READY_EVENT));
                });
            } catch (error: unknown) {
                if (cancelled) return;
                const message = error instanceof Error ? error.message : 'Erro ao conectar com Meta';
                notify.error(message);
            }
        };

        void run();
        return () => {
            cancelled = true;
        };
    }, [user?.id, canManageIntegrations]);

    // Gate: incompleto → onboarding; completo → limpa flags (tela de conclusão fica no page)
    useEffect(() => {
        if (!user?.id || isLoading || isSubscriptionBlocked) return;

        clearPendingOnboarding();

        if (isCompleted) return;

        if (isMetaOAuthFlowActive()) {
            if (canManageIntegrations && currentPage !== 'tracking') setCurrentPage('tracking');
            return;
        }

        // Portal do parceiro é independente do onboarding do CRM.
        if (isPartnerPage(currentPage)) return;

        if (currentPage !== 'onboarding') {
            setCurrentPage('onboarding');
        }
    }, [user?.id, isLoading, isCompleted, isSubscriptionBlocked, currentPage, setCurrentPage, canManageIntegrations]);

    useEffect(() => {
        const onForceOnboarding = () => {
            if (isMetaOAuthFlowActive()) return;
            if (completedRef.current) return;
            goToOnboardingWhenReady();
        };

        window.addEventListener('crm:billing-updated', onForceOnboarding);
        return () => {
            window.removeEventListener('crm:billing-updated', onForceOnboarding);
        };
    }, [goToOnboardingWhenReady]);

    return (
        <OnboardingGateProvider value={gateValue}>
            {children}
        </OnboardingGateProvider>
    );
};
